import { A } from '@adornis/base/env-info.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { BHFilter } from '../entities/bh-filter.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { User } from '../entities/user.js';
import { ParticipationState } from '../enums/enums.js';
import { printComponent } from '../print-with-user-session.js';
import { fillInEmpty, lookupEntityById } from '../util.js';

export const baseParticipationAggregation = [
  // join user
  { $lookup: lookupEntityById(User, 'userID', 'user') },
  { $unwind: '$user' },

  // join product
  { $lookup: lookupEntityById(Workshop, 'productID', 'product') },
  { $unwind: '$product' },

  // join userStammdaten
  {
    $lookup: {
      // join all user stammdaten
      from: UserStammdaten._collectionName,
      foreignField: 'userID',
      localField: 'userID',
      as: 'userStammdaten',
    },
  },
  {
    $addFields: {
      // only keep institution stammdaten
      userStammdaten: {
        $filter: {
          input: '$userStammdaten',
          as: 'stammdaten',
          cond: { $eq: ['$$stammdaten.institutionID', '$product.institutionID'] },
        },
      },
      institutionID: '$product.institutionID', // to match bh-filter
    },
  },
  { $set: { userStammdaten: { $first: '$userStammdaten' } } },

  // add fields
  {
    $addFields: {
      isNoShow: {
        $and: [{ $eq: ['$state', ParticipationState.IN] }],
      },
    },
  },
];

export const getParticipantsCount = registerQuery({
  type: () => Int,
  operationName: 'getParticipantsCount',
  params: [{ type: () => BHFilter, name: 'filter' }],
  resolve: (filter: BHFilter) => {
    return async () => {
      const participatonCollection = await getRawCollection(Participation._collectionName);

      const mongoFilter = {
        ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
        ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
      };

      const aggregation = [
        ...baseParticipationAggregation,
        { $match: mongoFilter },
        // count distinct users
        { $group: { _id: '$userID' } },
        { $count: 'total' },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();
      return result[0]?.total ?? 0;
    };
  },
});

export const getParticipationCount = registerQuery({
  type: () => Int,
  operationName: 'getParticipationCount',
  params: [
    { type: () => [String], name: 'states' },
    { type: () => BHFilter, name: 'filter' },
  ],
  resolve: (states: ParticipationState[], filter: BHFilter) => {
    return async () => {
      const stateFilter = (() => {
        if (states.length === 0) return {};
        return { state: { $in: states } };
      })();

      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            ...stateFilter,
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        { $count: 'total' },
      ];

      const participatonCollection = await getRawCollection(Participation._collectionName);
      const result = await participatonCollection.aggregate(aggregation).toArray();

      return result[0]?.total ?? 0;
    };
  },
});

export const getAverageParticipationsPerUser = registerQuery({
  type: () => Float,
  operationName: 'getAverageParticipationsPerUser',
  params: [
    { type: () => [String], name: 'states' },
    { type: () => BHFilter, name: 'filter' },
  ],
  resolve: (states: ParticipationState[], filter: BHFilter) => {
    return async () => {
      const doesMatchStateFilter = (() => {
        if (states.length === 0) return true;
        return { $in: ['$state', states] };
      })();

      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        { $addFields: { doesMatchStateFilter: doesMatchStateFilter } },
        { $group: { _id: '$userID', total: { $sum: { $cond: ['$doesMatchStateFilter', 1, 0] } } } }, // count per user
        { $group: { _id: null, averageTotal: { $avg: '$total' } } }, // calculate average
      ];

      const participatonCollection = await getRawCollection(Participation._collectionName);
      const result = await participatonCollection.aggregate(aggregation).toArray();

      return result[0]?.averageTotal ?? 0;
    };
  },
});

export const getUserProductRegistrationsOverTime = registerQuery({
  type: () => [[Int, Int]],
  operationName: 'getUserProductRegistrationsOverTime',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => Int, name: 'limit' },
  ],
  resolve: (filter: BHFilter, limit = 10) => {
    return async () => {
      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        {
          $project: {
            week: { $week: '$createdAt' },
            year: { $year: '$createdAt' },
          },
        },
        {
          $group: {
            _id: { week: '$week', year: '$year' },
            total: { $sum: 1 },
            week: { $first: '$week' },
            year: { $first: '$year' },
          },
        },
      ];

      const participatonCollection = await getRawCollection(Participation._collectionName);
      const result = await participatonCollection
        .aggregate(aggregation)
        .sort({ year: -1, week: -1 }) // sort desc to get most current results
        .limit(limit)
        .toArray();

      const registrationsOverTime = fillInEmpty(result as any);
      return registrationsOverTime.slice(-1 * limit).map(({ week, total }) => [week, total]);
    };
  },
});

export const getParticipationsOverTime = registerQuery({
  type: () => [[Int, Int]],
  operationName: 'getParticipationsOverTime',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => Int, name: 'limit' },
  ],
  resolve: (filter: BHFilter, limit = 10) => {
    return async () => {
      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            state: ParticipationState.PARTICIPATED,
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        {
          $project: {
            week: {
              $week: '$updatedAt',
            },
            year: {
              $year: '$updatedAt',
            },
          },
        },
        {
          $group: {
            _id: {
              week: '$week',
              year: '$year',
            },
            total: {
              $sum: 1,
            },
            week: {
              $first: '$week',
            },
            year: {
              $first: '$year',
            },
          },
        },
      ];

      const participatonCollection = await getRawCollection(Participation._collectionName);
      const result = await participatonCollection
        .aggregate(aggregation)
        .sort({ year: -1, week: -1 }) // sort desc to get most current results
        .limit(limit)
        .toArray();

      const registrationsOverTime = fillInEmpty(result as any);
      return registrationsOverTime.slice(-1 * limit).map(({ week, total }) => [week, total]);
    };
  },
});

@Entity()
export class TimeframeParticipationCountByStates extends AdornisEntity {
  public static override _class = 'TimeframeParticipationCountByStates';

  @Field(type => String)
  public timeframeID!: string;

  @DataLoadedField(type => Timeframe, doc => ({ _id: doc.timeframeID }))
  public timeframe!: Timeframe;

  @Field(type => [[String, Int]])
  public counts!: Array<[string, number]>;
}

export const getParticipationCountGroupedByTimeframeState = registerQuery({
  type: () => [TimeframeParticipationCountByStates],
  operationName: 'getParticipationCountGroupedByTimeframeState',
  params: [{ type: () => BHFilter, name: 'filter' }],
  resolve: (filter: BHFilter) => {
    return async (gqlFields: BaseQLSelectionSet<TimeframeParticipationCountByStates>) => {
      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        {
          $group: {
            _id: {
              timeframeID: '$product.timeframeID',
              state: '$state',
            },
            total: {
              $sum: 1,
            },
            timeframeID: {
              $first: '$product.timeframeID',
            },
            state: {
              $first: '$state',
            },
          },
        },
      ];

      const participatonCollection = await getRawCollection(Participation._collectionName);
      const result = await participatonCollection.aggregate(aggregation).toArray();

      const counts = result.reduce((acc, r) => {
        const timeframeID = r.timeframeID;
        if (!timeframeID) return acc;
        const tpCount = acc[timeframeID] || new TimeframeParticipationCountByStates({ timeframeID, counts: [] });
        acc[timeframeID] = tpCount;
        tpCount.counts.push([r.state, r.total]);
        return acc;
      }, {});

      return Object.values(counts);
    };
  },
});

export const getUserCount = registerQuery({
  type: () => Int,
  operationName: 'getUserCount',
  params: [{ type: () => [String], name: 'userTypes' }],
  resolve: (userTypes: string[]) => {
    return async () => {
      const aggregation = [
        { $match: { 'roles.name': { $in: userTypes } } }, //
        { $count: 'total' },
      ];

      const userCollection = await getRawCollection(User._collectionName);
      const result = await userCollection.aggregate(aggregation).toArray();

      return result[0]?.total ?? 0;
    };
  },
});

export const generateStatisticPdf = registerMutation({
  type: () => String,
  operationName: 'generateeStatisicPdf',
  params: [{ type: () => BHFilter, name: 'filter' }],
  resolve: (filter: BHFilter) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself()({ _id: 1, _class: 1, firstName: 1, lastName: 1, title: 1 });
      if (!user) throw new Error('user not found');

      const buffer = await printComponent('statistics-institution', {
        user,
        timeout: 25 * 1000,
        queryParams: {
          filter: btoa(JSON.stringify(filter.toJSON())),
        },
      });

      const institutionID = filter.institutionID ?? 'global';
      const name = `statistics-${institutionID}.pdf`;
      const file = await AdornisFile.createFromBuffer(buffer, name);

      return A.absoluteUrl(file.getServeLink() + '/' + name);
    };
  },
});
